import React, { useEffect, useState } from "react";
import { Card, Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import Chart from "react-apexcharts";
import axios from "axios";
import { API_ENDPOINTS } from "../../../../config/api";

const TotalUsersByType = () => {
  const [isChartLoaded, setChartLoaded] = useState(false);
  const [userData, setUserData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [loading, setLoading] = useState(true);

  const getDateRange = (period) => {
    const today = new Date();
    let from = null;
    let to = today.toISOString().split('T')[0];

    switch (period) {
      case "week":
        from = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        break;
      case "month":
        from = new Date(today.setMonth(today.getMonth() - 1)).toISOString().split('T')[0];
        break;
      case "year":
        from = new Date(today.setFullYear(today.getFullYear() - 1)).toISOString().split('T')[0];
        break;
      default:
        from = null;
    }
    return { from, to };
  };

  const fetchUserData = async (period) => {
    try {
      setLoading(true);
      const { from, to } = getDateRange(period);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;

      const response = await axios.get(API_ENDPOINTS.GET_USERS_BY_TYPE, { params });
      setUserData(response.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setChartLoaded(true);
    fetchUserData(selectedPeriod);
  }, [selectedPeriod]);

  // Series data for bar chart
  const series = userData ? Object.values(userData).map(type => type.total || 0) : [];

  // Define colors based on the image, roughly
  const chartColors = ['#E34234', '#26a69a', '#FF7518', '#2196f3', '#3f51b5'];

  // Get dynamic categories and assign colors
  const categories = userData ? Object.keys(userData) : [];
  const dynamicColors = categories.map((_, index) => chartColors[index % chartColors.length]);

  const options = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        distributed: true,
        borderRadiusApplication: 'around',
        borderRadiusWhenStacked: 'around',
        borderRadius: 8,
      }
    },
    colors: dynamicColors,
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#304758']
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        borderRadius: 20,
        padding: 5,
        opacity: 1,
        borderWidth: 1,
        borderColor: '#ccc'
      }
    },
    legend: {
      show: false,
    },
    grid: {
      show: true,
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
      position: 'back',
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      }
    },
    xaxis: {
      categories: categories,
      labels: {
        style: {
          fontSize: '12px',
          colors: dynamicColors
        }
      },
      axisBorder: {
        show: true,
        color: '#f1f1f1'
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      show: true,
      title: {
        text: 'Number of Users'
      },
      labels: {
        formatter: function (val) {
          return Math.round(val);
        }
      }
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " Users"
        }
      }
    }
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: "none",
          borderRadius: "7px",
          mb: "25px",
          padding: { xs: "18px", sm: "20px", lg: "25px" },
          background: "#fff",
          color: "#000",
          position: "relative",
          overflow: "hidden",
        }}
        className="rmui-card"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: "#000"
            }}
            className="text-black"
          >
            User Counts by Type
          </Typography>

          <FormControl sx={{ minWidth: 120 }}>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              size="small"
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isChartLoaded && !loading && userData ? (
          <>
            <Box sx={{ width: '100%', mt: 3 }}>
              <Chart
                options={options}
                series={[{ data: series }]}
                type="bar"
                height={350}
                width={"100%"}
              />
            </Box>
          </>
        ) : (
          <Typography>Loading chart data...</Typography>
        )}
      </Card>
    </>
  );
};

export default TotalUsersByType;
